// MasterTimelinePopup.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Paper,
  Typography,
  DialogTitle,
  Card,
  Divider,
  Stack,
  Fade,
  Slide,
  Chip
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Add as AddIcon,
  Schedule as ScheduleIcon,
  PlaylistAddCheck as PlaylistIcon,
  AutoMode as AutoIcon
} from '@mui/icons-material';
import CampaignTimelineEntry from './campaignTimelineEntry';
import useIsDesktop from '../../../../Hooks/useIsDesktop';

const baseDate = new Date();
const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const DEFAULT_EVENTS = [
  { 
    id: 'script-due', 
    objective: 'Script Due', 
    projected_date: addDays(baseDate, 2),
    status: 'incomplete',
    notes: '',
    manager_notes: '',
    manager_completed: false,
    will_delete: false
  },
  { 
    id: 'draft-due', 
    objective: 'Draft Due', 
    projected_date: addDays(baseDate, 4),
    status: 'incomplete',
    notes: '',
    manager_notes: '',
    manager_completed: false,
    will_delete: false
  },
  { 
    id: 'posting-date', 
    objective: 'Posting Date', 
    projected_date: addDays(baseDate, 6),
    status: 'incomplete',
    notes: '',
    manager_notes: '',
    manager_completed: false,
    will_delete: false
  },
];

export default function MasterTimelinePopup({
  open,
  onClose,
  masterTimeline,
  setMasterTimeline,
  onApplyToAllCreators,
}) {
  const [localTimeline, setLocalTimeline] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (open) {
      const initialTimeline = masterTimeline.length > 0 ? masterTimeline : DEFAULT_EVENTS;
      setLocalTimeline(initialTimeline);
      setHasChanges(false);
    }
  }, [open, masterTimeline]);

  const handleEditLocalEvent = (index, field, value) => {
    setLocalTimeline(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      const sorted = updated.sort((a, b) => new Date(a.projected_date) - new Date(b.projected_date));
      setHasChanges(true);
      return sorted;
    });
  };

  const handleDeleteLocalEvent = (index) => {
    setLocalTimeline(prev => {
      setHasChanges(true);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleAddLocalEvent = () => {
    setLocalTimeline(prev => {
      const newEvent = {
        id: Date.now(),
        objective: '',
        projected_date: new Date(),
        status: 'incomplete',
        notes: '',
        manager_notes: '',
        manager_completed: false,
        will_delete: false
      };
      const updated = [...prev, newEvent];
      const sorted = updated.sort((a, b) => new Date(a.projected_date) - new Date(b.projected_date));
      setHasChanges(true);
      return sorted;
    });
  };

  const handleSave = () => {
    setMasterTimeline(localTimeline);
    setHasChanges(false);
    onClose();
  };

  const handleCancel = () => {
    setHasChanges(false);
    onClose();
  };

  const handleApplyToAll = () => {
    onApplyToAllCreators(localTimeline);
    setHasChanges(false);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      fullWidth 
      maxWidth="md" 
      fullScreen={!isDesktop}
      PaperProps={{
        sx: {
          borderRadius: isDesktop ? 3 : 0,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        }
      }}
      TransitionComponent={isDesktop ? Fade : Slide}
      TransitionProps={!isDesktop ? { direction: "up" } : {}}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          pb: 2,
          background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
          color: 'white'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <ScheduleIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Master Timeline
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Create a template for all creators
            </Typography>
          </Box>
        </Stack>
        <IconButton 
          onClick={handleCancel} 
          size="large"
          sx={{ 
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: '#fafafa' }}>
        <Card 
          elevation={0}
          sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ 
            p: 3, 
            backgroundColor: 'white',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <PlaylistIcon color="primary" />
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Timeline Milestones
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {localTimeline.length} milestone{localTimeline.length !== 1 ? 's' : ''} planned
                  </Typography>
                </Box>
              </Stack>
              {hasChanges && (
                <Chip 
                  label="Unsaved Changes" 
                  color="warning" 
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Stack>
          </Box>

          <Box sx={{ p: 3, backgroundColor: 'white' }}>
            {localTimeline.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 6,
                backgroundColor: 'grey.50',
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'grey.300'
              }}>
                <ScheduleIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No milestones yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Add your first milestone to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddLocalEvent}
                  sx={{ borderRadius: 2 }}
                >
                  Add First Milestone
                </Button>
              </Box>
            ) : (
              <Stack spacing={2}>
                {localTimeline.map((event, index) => (
                  <Card
                    key={event.id}
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)'
                      }
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <CampaignTimelineEntry
                        event={event}
                        onEditField={(field, value) => handleEditLocalEvent(index, field, value)}
                        onDelete={() => handleDeleteLocalEvent(index)}
                      />
                    </Box>
                  </Card>
                ))}
                
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddLocalEvent}
                  sx={{ 
                    mt: 2,
                    borderRadius: 2,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    py: 1.5,
                    '&:hover': {
                      borderStyle: 'solid',
                      backgroundColor: 'primary.50'
                    }
                  }}
                >
                  Add Another Milestone
                </Button>
              </Stack>
            )}
          </Box>
        </Card>

        {localTimeline.length > 0 && (
          <Card 
            elevation={0}
            sx={{ 
              mt: 3,
              border: '1px solid',
              borderColor: 'success.light',
              borderRadius: 3,
              backgroundColor: 'success.50'
            }}
          >
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <AutoIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                Ready to Apply
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This timeline will be copied to all selected creators
              </Typography>
              {onApplyToAllCreators && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleApplyToAll}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Apply to All Creators
                </Button>
              )}
            </Box>
          </Card>
        )}
      </DialogContent>

      <Divider />
      
      <DialogActions sx={{ p: 3, backgroundColor: 'white' }}>
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button 
            onClick={handleCancel}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          
          <Box sx={{ flex: 1 }} />
          
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={localTimeline.length === 0}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Save Timeline
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}